// Navigation model of the V2 shell (plan §4.2): the restaurant drawer and the
// user drawer. Pure and DOM-free so vitest (node env) covers every rule; the
// entries, permissions, tiers and hidden rules moved verbatim from the legacy
// RestaurantMenu.vue / MainMenu.vue.

import { PermissionType } from '@/enums/PermissionType';
import { MemberRole } from '@/interfaces/IMember';

/** A drawer row that navigates. */
export interface INavLink {
  key: string;
  /** i18n key of the label. */
  titleKey: string;
  to: string;
  /** MDI icon name without the `mdi-` prefix (top-level rows only). */
  icon?: string;
  /** Any-of bitmask: a custom role needs at least one of these permissions. */
  permissions?: number;
  /** Lowest plan tier that includes it (Plus = 1, Pro = 2). */
  minTier?: number;
  /** «Nuevo» chip (replaces the plan badge while shown). */
  newChip?: boolean;
}

/**
 * A top-level drawer entry: a plain link (`to`, no children), a group
 * without a page (children only) or a page parent (`to` and children: its
 * sub-pages show while the route is inside `sectionPrefixes`).
 */
export interface INavGroup extends Omit<INavLink, 'to'> {
  icon: string;
  to?: string;
  children?: INavLink[];
  /** Paths under which the group counts as open (defaults to its children). */
  sectionPrefixes?: string[];
}

export interface INavRole {
  type: MemberRole;
  permissions?: number;
  allCajas: boolean;
}

export interface IRestaurantNavContext {
  stringId: string;
  country: string;
  restaurantId: number;
  effectiveTier: number;
  role: INavRole;
  isStaff: boolean;
  /** The club launch chip (written by useWhatsNew). */
  clubLaunchChip: boolean;
  /** The Menú V2 page is enabled for this store (the «Menú» entry is the page). */
  menuV2: boolean;
  /** Until when the «Menú» entry shows «Nuevo» (null: never). */
  menuV2NewUntil: Date | null;
  now: Date;
}

export type TNavBadge = 'new' | 'plus' | 'pro';

/** Stores (lowercase stringId) with access to the new POS devices page. */
const POS_DEVICES_ENABLED_STORES = [
  'bobas',
  'bobascordoba',
  'bobasprueba',
  'bobaslavalle',
  'theramenbuffet',
  'tokemaki',
];

/** Stores that see the emails page (besides staff). */
const EMAILS_ENABLED_RESTAURANT_IDS = [2, 14400];

type TNavLinkDraft = INavLink & { hidden?: boolean };

/** Drops hidden children, and groups left without any. */
function visible(
  group: INavGroup & { hidden?: boolean },
  children?: TNavLinkDraft[]
): INavGroup | null {
  if (group.hidden) return null;
  if (!children) return group;

  const shown = children
    .filter((child) => !child.hidden)
    .map(({ hidden, ...child }) => child);
  if (!shown.length && !group.to) return null;
  return { ...group, children: shown };
}

/** A statistics page (Plus, StatisticsView). */
function statsLink(base: string, titleKey: string, path: string): INavLink {
  return {
    key: `stats-${path}`,
    titleKey: `restaurant.menu.stats.${titleKey}`,
    to: `${base}/stats/${path}`,
    minTier: 1,
    permissions: PermissionType.StatisticsView,
  };
}

function menuGroup(ctx: IRestaurantNavContext, base: string): INavGroup {
  if (!ctx.menuV2) {
    return {
      key: 'menu',
      titleKey: 'restaurant.menu.menu',
      icon: 'silverware-fork-knife',
      children: [
        {
          key: 'menu-categories',
          titleKey: 'restaurant.menu.menu.categories',
          to: `${base}/categories`,
        },
        {
          key: 'menu-items',
          titleKey: 'restaurant.menu.menu.items',
          to: `${base}/items`,
        },
        {
          key: 'menu-options',
          titleKey: 'restaurant.menu.menu.options',
          to: `${base}/menu/options`,
        },
        {
          key: 'menu-settings',
          titleKey: 'restaurant.menu.menu.settings',
          to: `${base}/catalog/settings`,
          // The page itself needs it: without it custom roles bounced.
          permissions: PermissionType.CatalogManage,
        },
        {
          key: 'menu-prices',
          titleKey: 'restaurant.menu.menu.prices',
          to: `${base}/catalog/prices`,
          minTier: 1,
          permissions: PermissionType.CatalogManage,
        },
        {
          key: 'menu-reporting',
          titleKey: 'restaurant.menu.catalog.reportingCategories',
          to: `${base}/catalog/reporting-categories`,
          minTier: 1,
          permissions: PermissionType.CatalogManage,
        },
      ],
    };
  }

  return {
    key: 'menu',
    titleKey: 'restaurant.menu.menu',
    icon: 'silverware-fork-knife',
    to: `${base}/menu`,
    // No permission: every member sees the menu, as with the classic pages
    // (the page is read-only without catalog permissions).
    newChip: !!ctx.menuV2NewUntil && ctx.now < ctx.menuV2NewUntil,
    sectionPrefixes: [
      `${base}/menu`,
      `${base}/catalog/`,
      `${base}/items/classic`,
      `${base}/categories/classic`,
    ],
    children: [
      {
        key: 'menu-options',
        titleKey: 'restaurant.menu.menu.options',
        to: `${base}/menu/options`,
      },
      {
        key: 'menu-prices',
        titleKey: 'restaurant.menu.menu.prices',
        to: `${base}/catalog/prices`,
        minTier: 1,
        permissions: PermissionType.CatalogManage,
      },
      {
        key: 'menu-settings',
        titleKey: 'restaurant.menu.menu.settings',
        to: `${base}/catalog/settings`,
        permissions: PermissionType.CatalogManage,
      },
      {
        key: 'menu-reporting',
        titleKey: 'restaurant.menu.catalog.reportingCategories',
        to: `${base}/catalog/reporting-categories`,
        minTier: 1,
        permissions: PermissionType.CatalogManage,
      },
    ],
  };
}

/** The restaurant drawer, before the role filter. */
export function buildRestaurantNav(ctx: IRestaurantNavContext): INavGroup[] {
  const base = `/${ctx.stringId}`;

  const groups: (INavGroup | null)[] = [
    visible({
      key: 'dashboard',
      titleKey: 'restaurant.menu.dashboard',
      icon: 'view-dashboard-outline',
      to: `${base}/dashboard`,
    }),
    visible({
      key: 'pos',
      titleKey: 'restaurant.menu.pos',
      icon: 'cash-register',
      to: `${base}/pos`,
      minTier: 1,
      permissions: PermissionType.OrdersManage,
    }),
    visible({
      key: 'reservations',
      titleKey: 'restaurant.menu.reservations',
      icon: 'calendar-check',
      to: `${base}/reservations`,
      minTier: 2,
      permissions:
        PermissionType.ReservationsManage | PermissionType.ReservationsSettings,
    }),
    visible(
      {
        key: 'store',
        titleKey: 'restaurant.menu.store',
        icon: 'storefront-outline',
      },
      [
        {
          key: 'store-settings',
          titleKey: 'restaurant.menu.store.settings',
          to: `${base}/settings`,
          permissions: PermissionType.StoreSettings,
        },
        {
          key: 'store-design',
          titleKey: 'restaurant.menu.store.design',
          to: `${base}/design`,
          permissions: PermissionType.StoreSettings,
        },
        {
          key: 'store-qr',
          titleKey: 'restaurant.menu.store.qr',
          to: `${base}/qr`,
          permissions: PermissionType.QrGenerate,
        },
        {
          key: 'store-tables',
          titleKey: 'restaurant.menu.store.tables',
          to: `${base}/tables`,
          minTier: 1,
          permissions: PermissionType.StoreSettings,
        },
        {
          key: 'store-floor-plan',
          titleKey: 'restaurant.menu.store.floorPlan',
          to: `${base}/tables/plan`,
          minTier: 1,
          permissions: PermissionType.TablesManage,
        },
        {
          key: 'store-cajas',
          titleKey: 'restaurant.menu.store.cajas',
          to: `${base}/cajas`,
          minTier: 1,
          permissions: PermissionType.StoreSettings,
        },
        {
          key: 'store-pos-devices',
          titleKey: 'restaurant.menu.store.posDevices',
          to: `${base}/pos-devices`,
          minTier: 1,
          permissions: PermissionType.StoreSettings,
          hidden: !POS_DEVICES_ENABLED_STORES.includes(
            ctx.stringId.toLowerCase()
          ),
        },
        {
          key: 'store-branches',
          titleKey: 'restaurant.menu.store.branches',
          to: `${base}/branches`,
          minTier: 2,
          permissions: PermissionType.BranchSettings,
        },
        {
          key: 'store-ticket',
          titleKey: 'restaurant.menu.store.ticket',
          to: `${base}/ticket`,
          minTier: 1,
          permissions: PermissionType.BranchSettings,
        },
        {
          key: 'store-invoicing',
          titleKey: 'restaurant.menu.store.invoicing',
          to: `${base}/invoicing`,
          minTier: 2,
          permissions: PermissionType.BranchSettings,
          hidden: ctx.country !== 'AR',
        },
      ]
    ),
    menuGroup(ctx, base),
    visible(
      {
        key: 'sales',
        titleKey: 'restaurant.menu.sales',
        icon: 'bank-outline',
      },
      [
        {
          key: 'sales-caja',
          titleKey: 'caja',
          to: `${base}/caja`,
          minTier: 1,
          permissions: PermissionType.CajaManage,
        },
        {
          key: 'sales-orders',
          titleKey: 'restaurant.menu.sales.orders',
          to: `${base}/orders`,
          minTier: 1,
          permissions: PermissionType.OrdersHistoric,
        },
      ]
    ),
    visible(
      {
        key: 'inventory',
        titleKey: 'restaurant.menu.inventory',
        icon: 'food-fork-drink',
      },
      [
        {
          key: 'inventory-stock',
          titleKey: 'restaurant.menu.inventory.stock',
          to: `${base}/stock/items`,
          permissions: PermissionType.CatalogManage,
          minTier: 1,
        },
        {
          key: 'inventory-movements',
          titleKey: 'restaurant.menu.inventory.movements',
          to: `${base}/stock/movements`,
          permissions: PermissionType.CatalogManage,
          minTier: 1,
        },
      ]
    ),
    visible(
      {
        key: 'stats',
        titleKey: 'restaurant.menu.stats',
        icon: 'chart-line',
      },
      [
        statsLink(base, 'views', 'views'),
        statsLink(base, 'orders', 'orders'),
        statsLink(base, 'products', 'products'),
        statsLink(base, 'productsSeries', 'productsSeries'),
        statsLink(base, 'paymentMethods', 'paymentMethods'),
        statsLink(base, 'tips', 'tips'),
        // Store-wide caja reports: only for members who see every caja.
        { ...statsLink(base, 'cajas', 'cajas'), hidden: !ctx.role.allCajas },
        {
          ...statsLink(base, 'dayClose', 'day-close'),
          hidden: !ctx.role.allCajas,
        },
        {
          ...statsLink(base, 'daySummary', 'day-summary'),
          hidden: !ctx.role.allCajas,
        },
        statsLink(base, 'categorySales', 'categories'),
        statsLink(base, 'stockConsumption', 'stock-consumption'),
      ]
    ),
    visible(
      {
        key: 'users',
        titleKey: 'restaurant.menu.users',
        icon: 'account-group-outline',
      },
      [
        {
          key: 'users-staff',
          titleKey: 'restaurant.menu.users.staff',
          to: `${base}/members`,
          minTier: 1,
          permissions: PermissionType.UsersManage,
        },
      ]
    ),
    visible(
      {
        key: 'loyalty',
        titleKey: 'restaurant.menu.loyalty',
        icon: 'account-heart',
      },
      [
        {
          key: 'loyalty-club',
          titleKey: 'club',
          to: `${base}/club`,
          minTier: 1,
          permissions: PermissionType.ClubManage | PermissionType.ClubTopup,
          // The launch chip replaces «Plus» on this row while active
          // (written by useWhatsNew; never for ClubTopup-only users).
          newChip: ctx.clubLaunchChip,
        },
        {
          key: 'loyalty-customers',
          titleKey: 'restaurant.menu.users.customers',
          to: `${base}/customers`,
          permissions: PermissionType.OrdersManage,
        },
        {
          key: 'loyalty-coupons',
          titleKey: 'restaurant.menu.loyalty.coupons',
          to: `${base}/coupons`,
          permissions: PermissionType.StoreSettings,
        },
        {
          key: 'loyalty-emails',
          titleKey: 'restaurant.menu.loyalty.emails',
          to: `${base}/emails`,
          permissions: PermissionType.OrdersHistoric,
          hidden:
            !ctx.isStaff &&
            !EMAILS_ENABLED_RESTAURANT_IDS.includes(ctx.restaurantId),
        },
      ]
    ),
    visible(
      {
        key: 'extras',
        titleKey: 'restaurant.menu.extras',
        icon: 'tag-plus',
      },
      [
        {
          key: 'extras-stickers',
          titleKey: 'restaurant.menu.extras.stickers',
          to: `${base}/stickers`,
          permissions: PermissionType.CatalogManage,
        },
      ]
    ),
  ];

  return groups.filter((group): group is INavGroup => group !== null);
}

function allowed(permissions: number | undefined, role: INavRole): boolean {
  return !permissions || (permissions & (role.permissions ?? 0)) !== 0;
}

/**
 * Custom roles see an entry when they hold any of its permissions; owners
 * and admins see everything. A group without a page disappears when none of
 * its children is left.
 */
export function filterNavForRole(
  groups: INavGroup[],
  role: INavRole
): INavGroup[] {
  if (role.type !== MemberRole.Custom) return groups;

  const result: INavGroup[] = [];
  for (const group of groups) {
    if (!allowed(group.permissions, role)) continue;
    if (!group.children) {
      result.push(group);
      continue;
    }

    const children = group.children.filter((child) =>
      allowed(child.permissions, role)
    );
    if (!children.length && !group.to) continue;
    result.push({ ...group, children });
  }
  return result;
}

/**
 * Whether `path` is at `to`. Page parents match exactly (`/x/menu` is not
 * active on `/x/menu/options`); links also match their sub-paths.
 */
export function isNavLinkActive(
  path: string,
  to: string,
  exact = false
): boolean {
  if (path === to) return true;
  return !exact && path.startsWith(`${to}/`);
}

/** Whether the route is inside a group (it then starts expanded). */
export function isNavSectionOpen(path: string, group: INavGroup): boolean {
  if (group.sectionPrefixes)
    return group.sectionPrefixes.some(
      (prefix) => path === prefix.replace(/\/$/, '') || path.startsWith(prefix)
    );
  return (group.children ?? []).some((child) =>
    isNavLinkActive(path, child.to)
  );
}

/**
 * The key of the one entry to mark as current: the most specific match, so
 * `/x/tables/plan` marks «Plano» and not «Mesas» too.
 */
export function activeNavKey(path: string, groups: INavGroup[]): string | null {
  const candidates: { key: string; to: string; exact: boolean }[] = [];
  for (const group of groups) {
    if (group.to)
      candidates.push({
        key: group.key,
        to: group.to,
        exact: !!group.children,
      });
    for (const child of group.children ?? [])
      candidates.push({ key: child.key, to: child.to, exact: false });
  }

  const matches = candidates
    .filter((candidate) => isNavLinkActive(path, candidate.to, candidate.exact))
    .sort((a, b) => b.to.length - a.to.length);
  return matches.length ? matches[0].key : null;
}

/** «Nuevo», or the plan an entry needs when the store's tier is lower. */
export function navBadge(
  entry: Pick<INavLink, 'minTier' | 'newChip'>,
  effectiveTier: number
): TNavBadge | null {
  if (entry.newChip) return 'new';
  if (!entry.minTier || effectiveTier >= entry.minTier) return null;
  return entry.minTier === 1 ? 'plus' : 'pro';
}

/** The user drawer (outside a restaurant). */
export function buildUserNav(input: { isStaff: boolean }): INavLink[] {
  const links: INavLink[] = [
    {
      key: 'user-dashboard',
      titleKey: 'shell.userNav.dashboard',
      to: '/dashboard',
      icon: 'home-outline',
    },
    {
      key: 'user-account',
      titleKey: 'shell.userNav.account',
      to: '/me/settings',
      icon: 'account-outline',
    },
    {
      key: 'user-collections',
      titleKey: 'shell.userNav.collections',
      to: '/me/collections',
      icon: 'rhombus-split-outline',
    },
    {
      key: 'user-partner',
      titleKey: 'shell.userNav.partner',
      to: '/partner',
      icon: 'handshake-outline',
    },
  ];
  if (!input.isStaff) return links;

  return [
    ...links,
    {
      key: 'staff-dashboard',
      titleKey: 'shell.userNav.staffDashboard',
      to: '/staff/dashboard',
      icon: 'shield-account-outline',
    },
    {
      key: 'staff-income',
      titleKey: 'shell.userNav.staffIncome',
      to: '/staff/income',
      icon: 'cash',
    },
    {
      key: 'staff-payments',
      titleKey: 'shell.userNav.staffPayments',
      to: '/staff/payments',
      icon: 'credit-card-outline',
    },
    {
      key: 'staff-lookup',
      titleKey: 'shell.userNav.staffLookup',
      to: '/staff/lookup',
      icon: 'image-multiple-outline',
    },
  ];
}
